import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
/* global axios */
import api from '../services/apiService';
import { Helmet } from 'react-helmet';  // Import von React Helmet
import CountrySelect from './CountrySelect';


const formatDateTime = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const day     = String(date.getDate()).padStart(2, '0');
  const month   = String(date.getMonth() + 1).padStart(2, '0');
  const year    = date.getFullYear();
  const hours   = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const GERMAN_STATE_NAMES = {
  'baden-württemberg': 'Baden-Württemberg',
  'baden-wurttemberg': 'Baden-Württemberg',
  bavaria: 'Bayern',
  bayern: 'Bayern',
  berlin: 'Berlin',
  brandenburg: 'Brandenburg',
  bremen: 'Bremen',
  hamburg: 'Hamburg',
  hesse: 'Hessen',
  hessen: 'Hessen',
  'lower saxony': 'Niedersachsen',
  niedersachsen: 'Niedersachsen',
  'mecklenburg-vorpommern': 'Mecklenburg-Vorpommern',
  'north rhine-westphalia': 'Nordrhein-Westfalen',
  'nordrhein-westfalen': 'Nordrhein-Westfalen',
  'rhineland-palatinate': 'Rheinland-Pfalz',
  'rheinland-pfalz': 'Rheinland-Pfalz',
  saarland: 'Saarland',
  saxony: 'Sachsen',
  sachsen: 'Sachsen',
  'saxony-anhalt': 'Sachsen-Anhalt',
  'sachsen-anhalt': 'Sachsen-Anhalt',
  'schleswig-holstein': 'Schleswig-Holstein',
  thuringia: 'Thüringen',
  thüringen: 'Thüringen',
};

const normalizeGermanState = (stateName) =>
  GERMAN_STATE_NAMES[stateName?.trim().toLocaleLowerCase('de-DE')] || '';

const getAddressComponent = (addressComponents, type) =>
  addressComponents?.find(component => component.types.includes(type))?.long_name || '';


const CustomerForm = () => {
  const { customerId, prospectId } = useParams();
  const navigate = useNavigate();  // Um nach der Bearbeitung weiterzuleiten
  const [paymentConditions, setPaymentConditions] = useState([]);
  const [formData, setFormData] = useState({
    
    debtorNumber: '',
    buyerReference: '',
    company: '',
    companyAddon: '',
    salutation: '', // Anrede
    firstName: '',
    lastName: '',
    street: '',
    postalCode: '',
    city: '',
    country: 'Germany', // Land
    state: '', // Bundesland
    email: '',
    phone: '',
    mobile: '',
    website: '', // Webseite
    notes: '', // Interne Kundennotiz
    customerCategory: '', // Kundenkategorie
    customerGroup: '', // Kundengruppe
    billingAddressActive: false, // Zustand für die Checkbox
    billingCompany: '', // Firma für abweichende Rechnungsadresse
    billingCompanyAddon: '', // Firmenzusatz der abweichenden Rechnungsadresse
    billingStreet: '', // Straße und Hausnr.
    billingPostalCode: '', // PLZ
    billingCity: '',
    billingCountry: '', // Ort
    vatId: '',
     customerCategoryId: '', // Kundenkategorie-ID (Lookup!)
  customerGroupId: '',    // Kundengruppen-ID (Lookup!)
  customerOriginId: '',   // Herkunft-ID (Lookup!)
  statusCrm: 'lead',
  });

  // === HIER einfügen: ===
  const [customerCategories, setCustomerCategories] = useState([]);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [customerOrigins, setCustomerOrigins] = useState([]);

const companyInputRef = useRef(null);
const postalCodeInputRef = useRef(null);
const postalCodeWasEditedRef = useRef(false);
const [postalCodeLookup, setPostalCodeLookup] = useState({ status: 'idle', message: '' });
const [activeTab, setActiveTab] = useState('personal'); // Aktueller Tab: 'personal', 'contact', 'billing'


  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const response = await api.get(`/customers/${customerId}`);
        setFormData(prevData => ({
          ...prevData,
          ...response.data
        }));
      } catch (error) {
        console.error('Fehler beim Abrufen der Kundendaten:', error);
      }
    };
    if (customerId) fetchCustomer();
  }, [customerId]);

  useEffect(() => {
    if (!prospectId) return;
    api.get(`/prospects/${prospectId}`)
      .then(response => {
        const prospect = response.data;
        if (prospect.convertedCustomerId) {
          navigate(`/customerform/${prospect.convertedCustomerId}`, { replace: true });
          return;
        }
        setFormData(prevData => ({
          ...prevData,
          company: prospect.company || '',
          companyAddon: prospect.companyAddon || '',
          salutation: prospect.salutation || '',
          firstName: prospect.firstName || '',
          lastName: prospect.lastName || '',
          street: prospect.street || '',
          postalCode: prospect.postalCode || '',
          city: prospect.city || '',
          country: prospect.country || 'Germany',
          state: prospect.state || '',
          email: prospect.email || '',
          phone: prospect.phone || '',
          mobile: prospect.mobile || '',
          website: prospect.website || '',
          notes: prospect.notes || '',
          customerCategoryId: prospect.customerCategoryId || '',
          customerGroupId: prospect.customerGroupId || '',
          customerOriginId: prospect.customerOriginId || '',
          responsibleUser: prospect.responsibleUser || '',
          statusCrm: 'lead',
        }));
      })
      .catch(error => {
        console.error('Fehler beim Laden des Interessenten:', error);
        alert(error.response?.data?.error || 'Interessent konnte nicht geladen werden.');
      });
  }, [prospectId, navigate]);

  useEffect(() => {
    if (!customerId) {
      setContactList([]);
      return;
    }
    const fetchContacts = async () => {
      try {
        const response = await api.get(`/contacts/customer/${customerId}`);  // Anfrage an den richtigen Endpunkt
        setContactList(response.data);  // Setze die Liste der Ansprechpartner
      } catch (error) {
        console.error('Fehler beim Abrufen der Ansprechpartner:', error);
      }
    };
  
    fetchContacts();
  }, [customerId]);  // Stelle sicher, dass der Effekt neu ausgeführt wird, wenn die customerId sich ändert

  useEffect(() => {
    if (!customerId && !prospectId) {
      setFormData({
        debtorNumber: '',
        company: '',
        companyAddon: '',
        salutation: '',
        firstName: '',
        lastName: '',
        street: '',
        postalCode: '',
        city: '',
        country: 'Germany',
        state: '',
        email: '',
        phone: '',
        mobile: '',
        website: '',
        notes: '',
        customerCategory: '',
        customerGroup: '',
        billingAddressActive: false,
        billingCompany: '',
        billingCompanyAddon: '',
        billingStreet: '',
        billingPostalCode: '',
        billingCity: '',
    billingCountry: '',
        vatId: '',
        customerCategoryId: '',
  customerGroupId: '',
  customerOriginId: '',
  statusCrm: 'lead',
      });
      setContactList([]); // ❗ Ansprechpartner-Liste auch leeren
    }
  }, [customerId, prospectId]); // Konvertierungsdaten nicht mit dem leeren Formular überschreiben.
  
  // ─────────── Google Autocomplete ───────────
  useEffect(() => {
    if (!window.google?.maps?.places || !companyInputRef.current) return;

    const ac = new window.google.maps.places.Autocomplete(
      companyInputRef.current,
      { types: ['establishment'], componentRestrictions: { country: 'de' } }
    );
    ac.setFields(['name','address_components','formatted_phone_number','website']);
    ac.addListener('place_changed', () => {
      const p = ac.getPlace();
      const getComp = types =>
        p.address_components?.find(c => types.every(t => c.types.includes(t)))?.long_name || '';
      const state = normalizeGermanState(getComp(['administrative_area_level_1']));
      postalCodeWasEditedRef.current = false;
      setFormData(prev => ({
        ...prev,
        company:   p.name || prev.company,
        street:    `${getComp(['route'])} ${getComp(['street_number'])}`.trim(),
        postalCode:getComp(['postal_code']),
        city:      getComp(['locality']) || getComp(['postal_town']),
        state:     state || prev.state,
        country:   'Germany',
        phone:     p.formatted_phone_number || prev.phone,
        website:   p.website || prev.website
      }));
      setPostalCodeLookup({ status: 'idle', message: '' });
    });
  }, []);

  // Dynamische Google-Vorschläge für deutsche Postleitzahlen.
  useEffect(() => {
    if (
      activeTab !== 'personal' ||
      !window.google?.maps?.places ||
      !postalCodeInputRef.current
    ) {
      return undefined;
    }

    const autocomplete = new window.google.maps.places.Autocomplete(
      postalCodeInputRef.current,
      {
        types: ['(regions)'],
        componentRestrictions: { country: 'de' },
      }
    );
    autocomplete.setFields(['address_components']);

    const listener = autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const components = place.address_components;
      const postalCode = getAddressComponent(components, 'postal_code');

      if (!postalCode) {
        setPostalCodeLookup({
          status: 'error',
          message: 'Bitte einen Vorschlag mit Postleitzahl auswählen.',
        });
        return;
      }

      const city =
        getAddressComponent(components, 'locality') ||
        getAddressComponent(components, 'postal_town') ||
        getAddressComponent(components, 'administrative_area_level_3');
      const state = normalizeGermanState(
        getAddressComponent(components, 'administrative_area_level_1')
      );

      postalCodeWasEditedRef.current = false;
      setFormData(prev => ({
        ...prev,
        postalCode,
        city: city || prev.city,
        state: state || prev.state,
        country: 'Germany',
      }));
      setPostalCodeLookup({
        status: 'success',
        message: [city, state].filter(Boolean).join(', ') + ' automatisch übernommen.',
      });
    });

    return () => window.google.maps.event.removeListener(listener);
  }, [activeTab]);

  // Bei manueller Eingabe einer deutschen PLZ Ort und Bundesland auflösen.
  useEffect(() => {
    const postalCode = formData.postalCode.trim();
    const isGermany = !formData.country || formData.country === 'Germany';

    if (!postalCodeWasEditedRef.current || !isGermany || !/^\d{5}$/.test(postalCode)) {
      if (postalCodeWasEditedRef.current) {
        setPostalCodeLookup({ status: 'idle', message: '' });
      }
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(() => {
      if (!window.google?.maps?.Geocoder) {
        setPostalCodeLookup({
          status: 'error',
          message: 'PLZ konnte nicht automatisch geprüft werden. Ort und Bundesland bitte manuell eintragen.',
        });
        return;
      }

      setPostalCodeLookup({ status: 'loading', message: 'Ort und Bundesland werden ermittelt …' });
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode(
        {
          address: `${postalCode} Deutschland`,
          componentRestrictions: { country: 'DE', postalCode },
        },
        (results, status) => {
          if (cancelled || !postalCodeWasEditedRef.current) return;

          if (status !== 'OK' || !results?.length) {
            setPostalCodeLookup({
              status: 'error',
              message: 'Keine eindeutige Zuordnung gefunden. Ort und Bundesland bitte manuell eintragen.',
            });
            return;
          }

          const result = results.find(item =>
            getAddressComponent(item.address_components, 'postal_code') === postalCode
          ) || results[0];
          const components = result.address_components;
          const city =
            getAddressComponent(components, 'locality') ||
            getAddressComponent(components, 'postal_town') ||
            getAddressComponent(components, 'administrative_area_level_3');
          const state = normalizeGermanState(
            getAddressComponent(components, 'administrative_area_level_1')
          );

          if (!city && !state) {
            setPostalCodeLookup({
              status: 'error',
              message: 'Keine eindeutige Zuordnung gefunden. Ort und Bundesland bitte manuell eintragen.',
            });
            return;
          }

          setFormData(prev => {
            if (prev.postalCode.trim() !== postalCode) return prev;
            return {
              ...prev,
              city: city || prev.city,
              state: state || prev.state,
              country: 'Germany',
            };
          });
          postalCodeWasEditedRef.current = false;
          setPostalCodeLookup({
            status: 'success',
            message: [city, state].filter(Boolean).join(', ') + ' automatisch übernommen.',
          });
        }
      );
    }, 400);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [formData.postalCode, formData.country]);


  const [contactList, setContactList] = useState([]); // Ansprechpartner Liste
  const [contactData, setContactData] = useState({
    contactFirstName: '',
    contactLastName: '',
    contactEmail: '',
    contactPhone: '',
    contactMobile: '',
    contactFunction: '',
  });

  const [editContactId, setEditContactId] = useState(null); 
  const [isAddingContact, setIsAddingContact] = useState(false);  // Zustand zum Hinzufügen eines neuen Ansprechpartners
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

const sortedContacts = React.useMemo(() => {
  const result = [...contactList];

  if (sortConfig.key === 'function') {
    result.sort((a, b) => {
      const fnA = (a.contactFunction || '').toLowerCase();
      const fnB = (b.contactFunction || '').toLowerCase();

      // 1. nach Funktion sortieren (asc/desc)
      if (fnA < fnB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (fnA > fnB) return sortConfig.direction === 'asc' ? 1 : -1;

      // 2. wenn gleiche Funktion → nach Nachname (immer A-Z)
      const lnA = (a.contactLastName || '').toLowerCase();
      const lnB = (b.contactLastName || '').toLowerCase();

      if (lnA < lnB) return -1;
      if (lnA > lnB) return 1;
      return 0;
    });
  }

  return result;
}, [contactList, sortConfig]);

const handleSortByFunction = () => {
  setSortConfig(prev => {
    if (prev.key === 'function') {
      // Richtung umdrehen
      return {
        key: 'function',
        direction: prev.direction === 'asc' ? 'desc' : 'asc',
      };
    }
    // erster Klick → asc
    return { key: 'function', direction: 'asc' };
  });
};



  // Prüft, ob es für eine Person (Name + E-Mail) bereits einen Kontakt mit bestimmter Rolle gibt
const hasContactWithFunction = (baseContact, fn) => {
  return contactList.some(c =>
    c.contactEmail === baseContact.contactEmail &&
    c.contactFirstName === baseContact.contactFirstName &&
    c.contactLastName === baseContact.contactLastName &&
    c.contactFunction === fn
  );
};


  // Lade die Zahlungskonditionen, wenn die Komponente geladen wird
  useEffect(() => {
    const fetchPaymentConditions = async () => {
      try {
        const response = await api.get('/paymentconditions');
        setPaymentConditions(response.data);
      } catch (error) {
        console.error('Fehler beim Laden der Zahlungskonditionen:', error);
      }
    };

    fetchPaymentConditions();
  }, []);

useEffect(() => {
  api.get('/customerCategories').then(res => setCustomerCategories(res.data));
  api.get('/customerGroups').then(res => setCustomerGroups(res.data));
  api.get('/customerOrigins').then(res => setCustomerOrigins(res.data));
}, []);


  // Handling der Eingabewerte für Kunden
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handlePostalCodeChange = (e) => {
    const isGermany = !formData.country || formData.country === 'Germany';
    const value = isGermany
      ? e.target.value.replace(/\D/g, '').slice(0, 5)
      : e.target.value;

    postalCodeWasEditedRef.current = true;
    setFormData(prev => ({ ...prev, postalCode: value }));
  };

  // Handling der Eingabewerte für Ansprechpartner
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactData({
      ...contactData,
      [name]: value,
    });
  };

   // Ansprechpartner hinzufügen
   const handleAddContact = async () => {
    if (contactData.contactFirstName && contactData.contactLastName && contactData.contactEmail) {
      try {
        const response = await api.post(`/contacts/${customerId}`, {
          ...contactData,
        });
        setContactList([...contactList, response.data]);
        setContactData({ contactFirstName: '', contactLastName: '', contactEmail: '', contactPhone: '', contactMobile: '', contactFunction: '' });
        setIsAddingContact(false); // Setze das Hinzufügen zurück
        alert('Ansprechpartner erfolgreich hinzugefügt!');
      } catch (error) {
        console.error('Fehler beim Hinzufügen des Ansprechpartners:', error);
        alert('Fehler beim Hinzufügen des Ansprechpartners');
      }
    } else {
      alert('Bitte füllen Sie alle Felder für den Ansprechpartner aus!');
    }
  };
  // Ansprechpartner löschen
  const handleDeleteContact = async (id) => {
    try {
      // Sende DELETE-Anfrage an das Backend
      await api.delete(`/contacts/${id}`);
  
      // Lösche den Ansprechpartner aus der Liste im Frontend
      setContactList(contactList.filter((contact) => contact.id !== id));
    } catch (error) {
      console.error('Fehler beim Löschen des Ansprechpartners:', error);
      alert('Fehler beim Löschen des Ansprechpartners');
    }
  };


  // Ansprechpartner duplizieren mit anderer Funktion (Besteller <-> LW-Bediener)
const handleDuplicateContact = async (contact, targetFunction) => {
  try {
    // Falls es identische Person + Zielrolle schon gibt → nichts tun
    if (hasContactWithFunction(contact, targetFunction)) {
      alert(`Für diese Person existiert bereits ein Ansprechpartner mit Funktion "${targetFunction}".`);
      return;
    }

    const payload = {
      contactFirstName: contact.contactFirstName,
      contactLastName: contact.contactLastName,
      contactEmail: contact.contactEmail,
      contactPhone: contact.contactPhone,
      contactMobile: contact.contactMobile,
      contactFunction: targetFunction,
    };

    const response = await api.post(`/contacts/${customerId}`, payload);

    // neuen Ansprechpartner zur Liste hinzufügen
    setContactList([...contactList, response.data]);
    alert(`Ansprechpartner als "${targetFunction}" dupliziert.`);
  } catch (error) {
    console.error('Fehler beim Duplizieren des Ansprechpartners:', error);
    alert('Fehler beim Duplizieren des Ansprechpartners');
  }
};


  // Ansprechpartner bearbeiten
  const handleEditContact = (id) => {
    const contactToEdit = contactList.find((contact) => contact.id === id);
    setContactData({
      contactFirstName: contactToEdit.contactFirstName,
      contactLastName: contactToEdit.contactLastName,
      contactEmail: contactToEdit.contactEmail,
      contactPhone: contactToEdit.contactPhone,
      contactMobile: contactToEdit.contactMobile,
      contactFunction: contactToEdit.contactFunction,

    });
    setEditContactId(id);
  };

  // Speichern eines bearbeiteten Ansprechpartners
  const handleSaveContact = async () => {
    if (editContactId) {
      try {
        const updatedContact = {
          ...contactData,
        };
        const response = await api.put(`/contacts/${editContactId}`, updatedContact);
        setContactList(contactList.map(contact => contact.id === editContactId ? response.data : contact));
        setEditContactId(null); // Bearbeitungsmodus beenden
        setContactData({
          contactFirstName: '',
          contactLastName: '',
          contactEmail: '',
          contactPhone: '',
          contactMobile: '',
          contactFunction: ''
        });
      } catch (error) {
        console.error('Fehler beim Aktualisieren des Ansprechpartners:', error);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (customerId) {
        // Beim Aktualisieren eines bestehenden Kunden
        await api.put(`/customers/${customerId}`, formData);
        alert('Kunde erfolgreich aktualisiert!');
      } else if (prospectId) {
        const response = await api.post(`/prospects/${prospectId}/convert`, formData);
        alert(`Interessent wurde als Kunde ${response.data.customer.customerNumber} angelegt.`);
        navigate(`/customerform/${response.data.customer.id}`);
        return;
      } else {
        // Beim Erstellen eines neuen Kunden
        const { customerNumber, ...dataToSend } = formData; // Entferne 'customerNumber' aus den zu sendenden Daten
        const response = await api.post('/customers', dataToSend);
        alert('Kunde hinzugefügt: ' + response.data.id);
      }
      navigate('/customerlist');
    } catch (error) {
      console.error('Fehler beim Hinzufügen/Aktualisieren des Kunden:', error);
      // Zeige spezifische Fehlermeldungen an, falls vorhanden
      const errorMsg = error.response && error.response.data && error.response.data.error
        ? error.response.data.error
        : 'Fehler beim Hinzufügen/Aktualisieren des Kunden';
      alert(errorMsg);
    }
  };
  
  
  return (
    <div>
      <Helmet>
        <title>Kunde bearbeiten</title>
        <meta name="description" content="Kunde bearbeiten" />
      </Helmet>
      <h2>
        {customerId
          ? 'Kunde bearbeiten'
          : prospectId
            ? `Interessent I-${prospectId} in Kunden umwandeln`
            : 'Neuen Kunden anlegen'}
      </h2>

      {prospectId && (
        <div style={{ background: '#eef7ff', border: '1px solid #9ec5e8', padding: 10, marginBottom: 14 }}>
          Die Interessentendaten wurden in die normale Kundenanlage übernommen. Beim Speichern wird
          ein regulärer Kunde mit Kundennummer angelegt und die CRM-Historie übertragen.
        </div>
      )}

{customerId && (
  <div style={{ marginBottom: '1rem', fontSize: '0.9rem', color: '#555' }}>
    <div>
      <strong>Erstellt von:</strong>{" "}
      {formData.creator
        ? `${formData.creator.firstName} ${formData.creator.lastName}`
        : "Unbekannt"}{" "}
      am {formData.createdAt ? formatDateTime(formData.createdAt) : ""}
    </div>
    <div>
      <strong>Zuletzt geändert von:</strong>{" "}
      {formData.updator
        ? `${formData.updator.firstName} ${formData.updator.lastName}`
        : "Unbekannt"}{" "}
      am {formData.updatedAt ? formatDateTime(formData.updatedAt) : ""}
    </div>
  </div>
)}

      <form onSubmit={handleSubmit}>
        {/* Eingabefeld für die Firma ganz oben links */}
        <div className="company-input">
          <input
            type="text"
            name="company"
            ref={companyInputRef}
            value={formData.company}
            onChange={handleChange}
            placeholder="Firma*"
            required
          />
        </div>

        {/* Firmenzusatz linksbündig */}
        <div className="company-addon">
          <input
            type="text"
            name="companyAddon"
            value={formData.companyAddon}
            onChange={handleChange}
            placeholder="Firmenzusatz"
          />
        </div>

        {/* Tabs für die Sektionen als Karteireiter */}
        <div className="tabs">
          <button
            type="button"
            className={activeTab === 'personal' ? 'active' : ''}
            onClick={() => setActiveTab('personal')}
          >
            Kontakte und Adressen
          </button>
          {!prospectId && (
            <button
              type="button"
              className={activeTab === 'contact' ? 'active' : ''}
              onClick={() => setActiveTab('contact')}
            >
              Ansprechpartner
            </button>
          )}
          <button
            type="button"
            className={activeTab === 'billing' ? 'active' : ''}
            onClick={() => setActiveTab('billing')}
          >
            Buchhaltung
          </button>
        </div>

        {/* Kontakte und Adressen (drei Spalten Layout) */}
        {activeTab === 'personal' && (
          <div className="form-row">
            <div className="col">
              <input
                type="text"
                name="street"
                value={formData.street}
                onChange={handleChange}
                placeholder="Straße und Nr.*"
                required
              />
              <input
                type="text"
                name="postalCode"
                ref={postalCodeInputRef}
                value={formData.postalCode}
                onChange={handlePostalCodeChange}
                placeholder="PLZ*"
                inputMode={formData.country === 'Germany' ? 'numeric' : 'text'}
                autoComplete="postal-code"
                maxLength={formData.country === 'Germany' ? 5 : undefined}
                required
              />
              {postalCodeLookup.message && (
                <small
                  role="status"
                  style={{
                    color: postalCodeLookup.status === 'error' ? '#a33' : '#466',
                    margin: '-5px 0 10px',
                  }}
                >
                  {postalCodeLookup.message}
                </small>
              )}
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Ort*"
                required
              />

              {/* Bundesland als Dropdown */}
              <select
                name="state"
                value={formData.state}
                onChange={handleChange}
                
              >
                <option value="">Bundesland</option>
                <option value="Niedersachsen">Niedersachsen</option>
               
                <option value="Baden-Württemberg">Baden-Württemberg</option>
                <option value="Bayern">Bayern</option>
                <option value="Berlin">Berlin</option>
                <option value="Brandenburg">Brandenburg</option>
                <option value="Bremen">Bremen</option>
                <option value="Hamburg">Hamburg</option>
                <option value="Hessen">Hessen</option>
                <option value="Mecklenburg-Vorpommern">Mecklenburg-Vorpommern</option>
                
                <option value="Nordrhein-Westfalen">Nordrhein-Westfalen</option>
                <option value="Rheinland-Pfalz">Rheinland-Pfalz</option>
                <option value="Saarland">Saarland</option>
                <option value="Sachsen">Sachsen</option>
                <option value="Sachsen-Anhalt">Sachsen-Anhalt</option>
                <option value="Schleswig-Holstein">Schleswig-Holstein</option>
                <option value="Thüringen">Thüringen</option>
              </select>

              <CountrySelect
                name="country"
                aria-label="Land"
                value={formData.country}
                onChange={handleChange}
              />
             <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
  <input
    type="text"
    name="website"
    value={formData.website}
    onChange={handleChange}
    placeholder="Webseite"
    style={{ flex: 1 }}
  />
  {/* Nur anzeigen, wenn Webseite eingetragen ist */}
  {formData.website && (
    <button
      type="button"
      title="Webseite öffnen"
      onClick={() => {
        // URL ggf. mit https ergänzen
        let url = formData.website.trim();
        if (url && !/^https?:\/\//i.test(url)) {
          url = "https://" + url;
        }
        window.open(url, '_blank', 'noopener,noreferrer');
      }}
      style={{
        background: '#002B3E',
        color: 'white',
        border: 'none',
        borderRadius: '5px',
        padding: '6px 10px',
        cursor: 'pointer',
        fontSize: '18px',
        marginLeft: '4px',
        marginTop: '-10px',
      }}
    >
      🌐
    </button>
  )}
</div>

            </div>

            <div className="col">
              {/* Anrede als Dropdown, damit eine vorhandene Anrede entfernt werden kann */}
              <select
                name="salutation"
                value={formData.salutation || ''}
                onChange={handleChange}
                style={{
                  marginLeft: '0px',
                  marginTop: '0px',
                }}
              >
                <option value="">Keine Anrede</option>
                <option value="Herr">Herr</option>
                <option value="Frau">Frau</option>
              </select>

              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Vorname"
                
              />
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Nachname"
                
              />
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Telefon"
              
              />
              <input
                type="text"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="Mobil (+49177...)"
              />
            </div>

            <div className="col">
              {/* Das customerNumber-Feld wird nur angezeigt, wenn customerId vorhanden ist */}
              {customerId && (
                <input
                  type="text"
                  name="customerNumber"
                  value={formData.customerNumber || ''}
                  readOnly
                  placeholder="Kundennummer"
                />
              )}
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="E-Mail-Adresse"
               
              />
              
              <select
  name="customerCategoryId"
  value={formData.customerCategoryId}
  onChange={handleChange}
  style={{
      marginLeft: '0px',
      marginTop: '0px',
      }}
>
  <option value="">Kundenkategorie wählen</option>
  {customerCategories.map(cat =>
    <option key={cat.id} value={cat.id}>{cat.name}</option>
  )}
</select>

<select
  name="customerGroupId"
  value={formData.customerGroupId}
  onChange={handleChange}
   style={{
      marginLeft: '0px',
      marginTop: '0px',
      }}
>
  <option value="">Kundengruppe wählen</option>
  {customerGroups.map(group =>
    <option key={group.id} value={group.id}>{group.name}</option>
  )}
</select>

<select
  name="customerOriginId"
  value={formData.customerOriginId}
  onChange={handleChange}
   style={{
      marginLeft: '0px',
      marginTop: '0px',
      }}
>
  <option value="">Herkunft wählen</option>
  {customerOrigins.map(origin =>
    <option key={origin.id} value={origin.id}>{origin.name}</option>
  )}
</select>
{/* NEU: CRM-Status Dropdown */}
<select
  name="statusCrm"
  value={formData.statusCrm}
  onChange={handleChange}
  required
  style={{
    marginLeft: '0px',
    marginTop: '0px',
  }}
>
  <option value="lead">Lead</option>
  <option value="aktiv">Aktiv</option>
  <option value="stammkunde">Stammkunde</option>
  <option value="inaktiv">Inaktiv</option>
  <option value="gesperrt">Gesperrt</option>
</select>
              <label htmlFor="customerNotes" style={{ textAlign: 'left', marginBottom: 4 }}>
                Interne Kundennotiz
              </label>
              <textarea
                id="customerNotes"
                name="notes"
                value={formData.notes || ''}
                onChange={handleChange}
                rows={6}
                placeholder="Notizen zum Kunden …"
                style={{
                  width: '100%',
                  minHeight: 130,
                  padding: 10,
                  border: '1px solid #ddd',
                  borderRadius: 5,
                  boxSizing: 'border-box',
                  resize: 'vertical',
                  font: 'inherit',
                }}
              />
            </div>
          </div>
        )}
        {activeTab === 'contact' && !prospectId && (
          <div>
            <h3>Liste der Ansprechpartner</h3>
            <table>
              <thead>
                <tr>
                  <th>Vorname</th>
                  <th>Nachname</th>
                  <th>E-Mail</th>
                  <th>Telefon</th>
                  <th>Mobil</th>
                   <th
      onClick={handleSortByFunction}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
    >
      Funktion{' '}
      {sortConfig.key === 'function' &&
        (sortConfig.direction === 'asc' ? '▲' : '▼')}
    </th>
                  <th>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {/* Felder für das Hinzufügen eines neuen Ansprechpartners */}
                {isAddingContact && (
                  <tr>
                    <td>
                      <input
                        type="text"
                        name="contactFirstName"
                        value={contactData.contactFirstName}
                        onChange={handleContactChange}
                        placeholder="Vorname*"
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="contactLastName"
                        value={contactData.contactLastName}
                        onChange={handleContactChange}
                        placeholder="Nachname*"
                        required
                      />
                    </td>
                    <td>
                      <input
                        type="email"
                        name="contactEmail"
                        value={contactData.contactEmail}
                        onChange={handleContactChange}
                        placeholder="E-Mail*"
                       
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="contactPhone"
                        value={contactData.contactPhone}
                        onChange={handleContactChange}
                        placeholder="Telefon"
                       
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        name="contactMobile"
                        value={contactData.contactMobile}
                        onChange={handleContactChange}
                        placeholder="Mobil (+49177..)"
                       
                      />
                    </td>
                    <td>
        <select
          name="contactFunction"
          value={contactData.contactFunction}
          onChange={handleContactChange}
            style={{
    marginLeft: '0px',
    marginTop: '0px',
  }}
          required
        >
          <option value="">Bitte wählen* </option>
          <option value="Besteller">Besteller</option>
          <option value="LW-Bediener">LW-Bediener</option>
          <option value="Rechnungsempfang">Rechnungsempfang</option>
        </select>
      </td>
                    <td>
                      <button
                        type="button"
                        onClick={handleAddContact}
                        style={{
                          fontSize: '15px',
                          cursor: 'pointer',
                          padding: '5px 10px',
                          background: '#28a745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '5px',
                        }}
                      >
                        save
                      </button>
                    </td>
                  </tr>
                )}
                {sortedContacts.map((contact) => (
                  <tr key={contact.id}>
                    <td>
                      {editContactId === contact.id ? (
                        <input
                          type="text"
                          name="contactFirstName"
                          value={contactData.contactFirstName}
                          onChange={handleContactChange}
                        />
                      ) : (
                        contact.contactFirstName
                      )}
                    </td>
                    <td>
                      {editContactId === contact.id ? (
                        <input
                          type="text"
                          name="contactLastName"
                          value={contactData.contactLastName}
                          onChange={handleContactChange}
                        />
                      ) : (
                        contact.contactLastName
                      )}
                    </td>
                    <td>
                      {editContactId === contact.id ? (
                        <input
                          type="email"
                          name="contactEmail"
                          value={contactData.contactEmail}
                          onChange={handleContactChange}
                        />
                      ) : (
                        contact.contactEmail
                      )}
                    </td>
                    <td>
                      {editContactId === contact.id ? (
                        <input
                          type="text"
                          name="contactPhone"
                          value={contactData.contactPhone}
                          onChange={handleContactChange}
                        />
                      ) : (
                        contact.contactPhone
                      )}
                    </td>
                    <td>
                      {editContactId === contact.id ? (
                        <input
                          type="text"
                          name="contactMobile"
                          value={contactData.contactMobile}
                          onChange={handleContactChange}
                        />
                      ) : (
                        contact.contactMobile
                      )}
                    </td>
                    <td>
  {editContactId === contact.id ? (
    <select
      name="contactFunction"
      value={contactData.contactFunction}
      onChange={handleContactChange}
    >
      <option value="">Bitte wählen</option>
      <option value="Besteller">Besteller</option>
      <option value="LW-Bediener">LW-Bediener</option>
      <option value="Rechnungsempfang">Rechnungsempfang</option>
    </select>
  ) : (
    contact.contactFunction // Zeigt die gespeicherte Funktion an, wenn nicht bearbeitet
  )}
</td>
                    <td>
  {editContactId === contact.id ? (
    // BEARBEITUNGSMODUS: Save + Abbrechen
    <div style={{ display: 'flex', gap: '5px' }}>
      <button
        type="button"
        onClick={handleSaveContact}
        style={{
          fontSize: '15px',
          cursor: 'pointer',
          padding: '5px 6px',
          background: '#28a745',
          color: '#fff',
          border: 'none',
          borderRadius: '5px'
        }}
      >save</button>
      <button
        type="button"
        onClick={() => {
          setEditContactId(null);
          setContactData({
            contactFirstName: '',
            contactLastName: '',
            contactEmail: '',
            contactPhone: '',
            contactMobile: '',
            contactFunction: ''
          });
        }}
        style={{
          fontSize: '15px',
          cursor: 'pointer',
          padding: '5px 6px',
          background: '#888',
          color: '#fff',
          border: 'none',
          borderRadius: '5px'
        }}
      >X</button>
    </div>
  ) : (
    // NORMALMODUS: Edit + Löschen
    <div style={{ display: 'flex', gap: '5px' }}>
      <button
        type="button"
        onClick={() => handleEditContact(contact.id)}
        style={{
          fontSize: '15px',
          cursor: 'pointer',
          padding: '5px 10px',
          background: '#28a745',
          color: '#fff',
          border: 'none',
          borderRadius: '5px'
        }}
      >edit</button>
{/* Duplizieren-Button: zeigt passende Zielrolle an */}
      {contact.contactFunction === 'Besteller' && (
        <button
          type="button"
          onClick={() => handleDuplicateContact(contact, 'LW-Bediener')}
          style={{
            fontSize: '10px',
            cursor: 'pointer',
            padding: '10px 10px',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
          }}
        >
          als Bediener
        </button>
      )}

      {contact.contactFunction === 'LW-Bediener' && (
        <button
          type="button"
          onClick={() => handleDuplicateContact(contact, 'Besteller')}
          style={{
            fontSize: '10px',
            cursor: 'pointer',
            padding: '10px 10px',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: '5px',
          }}
        >
          als Besteller
        </button>
      )}

      <button
        type="button"
        onClick={() => handleDeleteContact(contact.id)}
        style={{
           fontSize: '10px',
                          cursor: 'pointer',
                          padding: '10px 10px',
                          background: '#FF4300',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '5px',
                          marginLeft: 'auto',  // Verschiebt den Button nach rechts
        }}
      >X</button>
    </div>
  )}
</td>

                  </tr>
                ))}
                <tr>
                <td colSpan="7" style={{ textAlign: 'right' }}>
                    <button type="button" onClick={() => setIsAddingContact(true)}
                      style={{
                        fontSize: '20px',
                        cursor: 'pointer',
                        padding: '10px 15px',
                        background: '#28a745',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '5px',
                      }}>+</button>
                  </td>

                  
                </tr>
              </tbody>
            </table>
          </div>
        )}

        
        {/* Buchhaltungsinformationen - Nur Debitorennummer */}
        {activeTab === 'billing' && (
  <div className="form-row">
    {/* Linke Spalte: Buchhaltungsdaten und Käuferreferenz */}
    <div className="col">
      <input
        type="text"
        name="debtorNumber"
        value={formData.debtorNumber}
        onChange={handleChange}
        placeholder="Debitorennummer"
        // required //
      />
      <input
        type="text"
        name="vatId"
        value={formData.vatId}
        onChange={handleChange}
        placeholder="Steuernummer"
        // required //
      />
      <select
        name="paymentConditionId"
        value={formData.paymentConditionId || ''}
        style={{
          
          cursor: 'pointer',
          padding: '10px 10px',
          borderRadius: '5px',
          marginLeft: 'auto',
          marginTop: '0px',  // Verschiebt den Button nach rechts
        }}
        onChange={handleChange}
        // required //
      >
        <option value="">Bitte Zahlungskondition wählen</option>
        {paymentConditions.map((pc) => (
          <option key={pc.id} value={pc.id}>
            {pc.name}
          </option>
        ))}
      </select>
      <label htmlFor="buyerReference">Käuferreferenz / Leitweg-ID</label>
      <input
        type="text"
        id="buyerReference"
        name="buyerReference"
        value={formData.buyerReference || ''}
        placeholder={formData.debtorNumber ? `Standard: ${formData.debtorNumber}` : 'Käuferreferenz / Leitweg-ID'}
        onChange={handleChange}
        maxLength={255}
        aria-describedby="buyerReferenceHelp"
      />
      <small id="buyerReferenceHelp">
        Wird in neue Rechnungen übernommen. Wenn das Feld leer bleibt, wird die Debitorennummer verwendet.
      </small>
    </div>

    <div className="col" />

    {/* Rechte Spalte: Checkbox für abweichende Rechnungsadresse */}
    <div className="col">
      {/* Checkbox für "abweichende Rechnungsadresse aktiv" */}
      <label>
        <input
          type="checkbox"
          name="billingAddressActive"
          checked={formData.billingAddressActive}
          onChange={(e) => {
            // Aktualisiere den Zustand der Checkbox
            setFormData({
              ...formData,
              billingAddressActive: e.target.checked,
            });
          }}
        />
        Abweichende Rechnungsadresse aktiv
      </label>

      {/* Wenn "abweichende Rechnungsadresse aktiv" gewählt ist, zeige die Adresseingabefelder */}
      {formData.billingAddressActive && (
        <>
          <input
            type="text"
            name="billingCompany"
            value={formData.billingCompany || ''}
            onChange={handleChange}
            placeholder="Firma"
          />
          <input
            type="text"
            name="billingCompanyAddon"
            value={formData.billingCompanyAddon || ''}
            onChange={handleChange}
            placeholder="Firmenzusatz"
          />
          <input
            type="text"
            name="billingStreet"
            value={formData.billingStreet || ''}
            onChange={handleChange}
            placeholder="Straße und Hausnr.*"
            required={formData.billingAddressActive}
          />
          <input
            type="text"
            name="billingPostalCode"
            value={formData.billingPostalCode || ''}
            onChange={handleChange}
            placeholder="PLZ*"
            required={formData.billingAddressActive}
          />
          <input
            type="text"
            name="billingCity"
            value={formData.billingCity || ''}
            onChange={handleChange}
            placeholder="Ort*"
            required={formData.billingAddressActive}
          />
          <label>Land der Rechnungsadresse
            <input type="text" name="billingCountry" value={formData.billingCountry || ''}
              onChange={handleChange} placeholder="Leer = Kundenland; z. B. AT, Schweiz" />
          </label>
        </>
      )}
    </div>
  </div>
)}
  
   
        {/* Absenden */}
<div>
  <button type="submit">
    {customerId ? 'Kunde aktualisieren' : 'Kunde hinzufügen'}
  </button>
</div>
      </form>
    </div>
  );
};

export default CustomerForm;
